import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import type { ReactNode } from 'react'

interface ProtectedRouteProps {
  children: ReactNode
  requireEmailVerification?: boolean
}

export function ProtectedRoute({
  children,
  requireEmailVerification = true
}: ProtectedRouteProps) {
  const { user, loading, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (loading) return // Wait for auth state to load

    if (!isAuthenticated) {
      // Store the attempted location for redirect after login
      const redirectTo = location.pathname !== '/' ? location.pathname : '/dashboard'
      navigate('/login', {
        replace: true,
        state: { from: redirectTo }
      })
      return
    }

    if (requireEmailVerification && user && !user.email_verified) {
      // Redirect to email verification page if email is not verified
      navigate('/verify-email', { replace: true })
      return
    }
  }, [loading, isAuthenticated, user, navigate, location, requireEmailVerification])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return null // Will redirect, so don't render anything
  }

  if (requireEmailVerification && user && !user.email_verified) {
    return null // Will redirect to email verification
  }

  return <>{children}</>
}

